import type { LibrarySortMode } from '@listening/frontend/stores/library-sort-store';
import * as m from '@/paraglide/messages';

export { librarySortModes as SORT_MODES } from '@listening/frontend/stores/library-sort-store';

/** Continue-first needs the home page's continue-listening set, so folder
 * views offer the four orderings that stand on their own. */
export const FOLDER_SORT_MODES: ReadonlyArray<LibrarySortMode> = [
  'newest',
  'oldest',
  'a-z',
  'z-a',
];

/** What a folder view shows for a persisted mode it cannot offer. */
export const folderSortMode = (mode: LibrarySortMode) =>
  mode === 'continue-first' ? 'newest' : mode;

export const sortLabel = (mode: LibrarySortMode) => {
  switch (mode) {
    case 'newest': {
      return m.dashboard_sort_newest();
    }
    case 'oldest': {
      return m.dashboard_sort_oldest();
    }
    case 'a-z': {
      return m.dashboard_sort_az();
    }
    case 'z-a': {
      return m.dashboard_sort_za();
    }
    case 'continue-first': {
      return m.dashboard_continue_listening();
    }
    default: {
      return mode satisfies never;
    }
  }
};

/**
 * Rows arrive in server order for every mode; `continue-first` is the one
 * client-side step, pinning the loaded rows that belong to the
 * continue-listening set on top in that set's own (most recently played) order.
 */
export function pinContinueListening<T extends { id: string }>(
  items: ReadonlyArray<T>,
  mode: LibrarySortMode,
  continueItems: ReadonlyArray<{ id: string }>,
): ReadonlyArray<T> {
  if (mode !== 'continue-first') return items;
  const loaded = new Map(items.map((item) => [item.id, item]));
  const pinned: Array<T> = [];
  for (const { id } of continueItems) {
    const row = loaded.get(id);
    if (row) pinned.push(row);
  }
  const pinnedIds = new Set(pinned.map((item) => item.id));
  return [...pinned, ...items.filter((item) => !pinnedIds.has(item.id))];
}
